import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Divider,
    Switch,
    FormControlLabel,
    Button
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    ChangeCircle,
    CheckCircle,
    History,
    SettingsBackupRestore
} from '@mui/icons-material';
import { useJourney } from 'api/Journeys/journey_queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unsetDraftChanges } from 'api/Journeys';
import useConfirmSidebar from 'hooks/useConfirmSidebar';

const ChangeIcon = ({ hasChanges, changes }) => {
    if (hasChanges) {
        return <ChangeCircle color="warning" sx={{ fontSize: 16 }} />;
    }
    return <CheckCircle color="success" sx={{ fontSize: 16 }} />;
};

// Function to calculate cumulative changes across all nested sections
const getCumulativeChanges = (section) => {
    let totalChanges = (section.changes || []).length;
    
    if (section.sections) {
        Object.values(section.sections).forEach(subsections => {
            if (Array.isArray(subsections)) {
                subsections.forEach(subsection => {
                    totalChanges += getCumulativeChanges(subsection);
                });
            } else {
                totalChanges += getCumulativeChanges(subsections);
            }
        });
    }
    
    return totalChanges;
};

const SectionItem = ({ section, level = 0, showOnlyChanged = true }) => {
    // Default to collapsed if section has no changes, expanded if it has changes
    const [expanded, setExpanded] = useState(level < 1 && section.has_changes);
    const hasSubsections = section.sections && Object.keys(section.sections).length > 0;
    const hasChanges = section.has_changes;
    const changes = section.changes || [];
    const cumulativeChanges = getCumulativeChanges(section);

    const handleToggle = () => {
        setExpanded(!expanded);
    };

    // Filter subsections based on showOnlyChanged setting
    const getFilteredSubsections = () => {
        if (!hasSubsections) return {};
        
        // If showing all, return all subsections
        if (!showOnlyChanged) {
            return section.sections;
        }
        
        // If showing only changed, filter subsections
        const filtered = {};
        Object.entries(section.sections).forEach(([key, subsections]) => {
            if (Array.isArray(subsections)) {
                const filteredArray = subsections.filter(subsection => subsection.has_changes);
                if (filteredArray.length > 0) {
                    filtered[key] = filteredArray;
                }
            } else {
                if (subsections.has_changes) {
                    filtered[key] = subsections;
                }
            }
        });
        return filtered;
    };

    const filteredSubsections = getFilteredSubsections();
    const hasFilteredSubsections = Object.keys(filteredSubsections).length > 0;
    const shouldShowExpandButton = hasSubsections && (showOnlyChanged ? hasFilteredSubsections : true);

    // Don't render this section if it has no changes and we're only showing changed items
    if (showOnlyChanged && !hasChanges && !hasFilteredSubsections) {
        return null;
    }

    const getSectionDisplayName = (sectionName) => {
        const nameMap = {
            'flow': 'Journey Flow',
            'flow_action_triggers': 'Event Triggers',
            'dropout_filter': 'Dropout Filter',
            'step': 'Step',
            'bypass_filter': 'Bypass Filter',
            'step_deps': 'Step Dependencies',
            'wait': 'Wait Settings',
            'strategy': 'Strategy'
        };
        return nameMap[sectionName] || sectionName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getChangeDescription = (change) => {
        const changeMap = {
            'change_is_coming': 'Changes pending',
            'updated_draft': 'Draft updated',
            'created': 'Created',
            'updated': 'Updated',
            'deleted': 'Deleted'
        };
        return changeMap[change.code] || change.code.replace(/_/g, ' ');
    };

    return (
        <Box>
            <ListItem
                sx={{
                    pl: 1 + (level * 1.5),
                    py: 0.25,
                    minHeight: 32
                }}
            >
                <ListItemIcon sx={{ minWidth: 32 }}>
                    <ChangeIcon hasChanges={hasChanges} changes={changes} />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontWeight: hasChanges ? 'medium' : 'normal' }}>
                                {getSectionDisplayName(section.section_name)}
                                {section.model_id && ` (ID: ${section.model_id})`}
                            </Typography>
                            {cumulativeChanges > 0 && (
                                <Chip
                                    label={cumulativeChanges}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 20, fontSize: '0.75rem' }}
                                />
                            )}
                        </Box>
                    }
                    secondary={
                        hasChanges && changes.length > 0 && (
                            <Box sx={{ mt: 0.25 }}>
                                {changes.map((change, index) => (
                                    <Chip
                                        key={index}
                                        label={getChangeDescription(change)}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        sx={{ 
                                            mr: 0.5, 
                                            mb: 0.25, 
                                            height: 16, 
                                            fontSize: '0.65rem' 
                                        }}
                                    />
                                ))}
                            </Box>
                        )
                    }
                />
                {shouldShowExpandButton && (
                    <IconButton
                        size="small"
                        onClick={handleToggle}
                        sx={{ ml: 1 }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                )}
            </ListItem>
            
            {shouldShowExpandButton && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {Object.entries(filteredSubsections).map(([key, subsections]) => (
                            <Box key={key}>
                                {Array.isArray(subsections) ? (
                                    subsections.map((subsection, index) => (
                                        <SectionItem
                                            key={`${key}-${index}`}
                                            section={subsection}
                                            level={level + 1}
                                            showOnlyChanged={showOnlyChanged}
                                        />
                                    ))
                                ) : (
                                    <SectionItem
                                        key={key}
                                        section={subsections}
                                        level={level + 1}
                                        showOnlyChanged={showOnlyChanged}
                                    />
                                )}
                            </Box>
                        ))}
                    </List>
                </Collapse>
            )}
        </Box>
    );
};

const JourneyChangesSidebar = ({ hideSidebar }) => {
    const { id: journeyIdStr } = useParams();
    const journeyId = parseInt(journeyIdStr);
    const queryClient = useQueryClient();
    const confirmSidebar = useConfirmSidebar();
    
    const { data: journey, isSuccess } = useJourney(journeyId);
    const [showOnlyChanged, setShowOnlyChanged] = useState(true);

    const revertDraftChanges = useMutation(unsetDraftChanges, {
        onSuccess: data => {
            queryClient.invalidateQueries(['journeys', journeyId]);
            queryClient.setQueryData(['journeys', journeyId, 'adjacencyList', 'draft'], data);
        }
    });

    const onRevertDraftChanges = () => {
        confirmSidebar({
            title: 'Revert Draft Changes',
            description: 'Are you sure you want to revert all draft changes? This action cannot be undone.',
            confirmationText: 'Discard all Draft Changes',
            cancellationText: 'Cancel'
        }).then(() => {
            revertDraftChanges.mutate({ id: journeyId });
        }).catch(() => {
            // User cancelled, do nothing
        });
    };

    if (!isSuccess || !journey || !journey.changes) {
        return (
            <div className='flow-sidebar' style={{width: '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                        <History/>
                        Journey Changes
                    </Typography>
                </Box>
                <Box className='flow-sidebar__body'>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                        No changes data available
                    </Typography>
                </Box>
            </div>
        );
    }

    const { changes, has_changes, sections } = journey.changes;
    
    // Calculate cumulative changes across all sections
    const getTotalCumulativeChanges = () => {
        let total = 0;
        if (sections) {
            Object.values(sections).forEach(sectionData => {
                if (Array.isArray(sectionData)) {
                    sectionData.forEach(section => {
                        total += getCumulativeChanges(section);
                    });
                } else {
                    total += getCumulativeChanges(sectionData);
                }
            });
        }
        return total;
    };
    
    const totalCumulativeChanges = getTotalCumulativeChanges();

    return (
        <div className='flow-sidebar' style={{width: '15vw'}}>
            <Box className='flow-sidebar__header'>
                <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                    <History/>
                    Journey Changes
                </Typography>
            </Box>
            <Box className='flow-sidebar__body'>
                <Box sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                        <ChangeIcon hasChanges={totalCumulativeChanges > 0} changes={changes} />
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {totalCumulativeChanges > 0 ? (
                                <>
                                    Journey has 
                                    <Chip
                                        label={`${totalCumulativeChanges} change${totalCumulativeChanges !== 1 ? 's' : ''}`}
                                        size="small"
                                        color="warning"
                                        sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                    pending changes
                                </>
                            ) : (
                                'No pending changes'
                            )}
                        </Typography>
                        {has_changes && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!showOnlyChanged}
                                        onChange={(e) => setShowOnlyChanged(!e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Show All"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Box>

                    {has_changes && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                                Change Summary
                            </Typography>
                            <Box>
                                {changes.map((change, index) => (
                                    <Chip
                                        key={index}
                                        label={change.code.replace(/_/g, ' ')}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Section Details
                    </Typography>
                    
                    <List dense sx={{ py: 0 }}>
                        {sections && Object.entries(sections).map(([key, sectionData]) => (
                            <Box key={key}>
                                {Array.isArray(sectionData) ? (
                                    sectionData.map((section, index) => (
                                        <SectionItem
                                            key={`${key}-${index}`}
                                            section={section}
                                            level={0}
                                            showOnlyChanged={showOnlyChanged}
                                        />
                                    ))
                                ) : (
                                    <SectionItem
                                        key={key}
                                        section={sectionData}
                                        level={0}
                                        showOnlyChanged={showOnlyChanged}
                                    />
                                )}
                            </Box>
                        ))}
                    </List>
                    
                    {has_changes && journey.status === 'active' && (
                        <Button
                            variant="contained"
                            color="error"
                            size="large"
                            fullWidth
                            startIcon={<SettingsBackupRestore />}
                            onClick={onRevertDraftChanges}
                            disabled={revertDraftChanges.isLoading}
                            sx={{ mt: 3, py: 1.5 }}
                        >
                            Discard All Changes
                        </Button>
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default JourneyChangesSidebar;
